import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  useGetCenterApplicationQuery,
  useUpdateCenterApplicationMutation
} from '../../redux/services/centerApplicationService'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { ClockIcon } from '@heroicons/react/24/outline'
import { useAppSelector } from '../../redux/hooks'
import AdminPageLayout from '../../components/admin/AdminPageLayout'
import CenterTypeBadge from '../../components/admin/CenterTypeBadge'
import StatusIcon from '../../components/admin/StatusIcon'
import StatusRow from '../../components/admin/StatusRow'
import AdminLoadingState from '../../components/admin/AdminLoadingState'
import AdminErrorState from '../../components/admin/AdminErrorState'
import BackToDashboardLink from '../../components/admin/BackToDashboardLink'
import EmployeesTable from '../../components/admin/EmployeesTable'
import { usePatientStats } from '../../hooks/usePatientStats'
import type { CenterType } from '../../constants/centerTypes'

export default function ApplicationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    data: application,
    isLoading,
    error
  } = useGetCenterApplicationQuery(Number(id))
  const [updateApplication] = useUpdateCenterApplicationMutation()
  const user = useAppSelector((state) => state.user)
  const { totalSCPatients, totalSCAPatients, totalOtherPatients } =
    usePatientStats(application)

  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [isAssociateApproval, setIsAssociateApproval] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminLoadingState type="detail" />
      </AdminPageLayout>
    )
  }

  if (error || !application) {
    return (
      <AdminPageLayout maxWidth="6xl">
        <AdminErrorState message="Error loading application" />
      </AdminPageLayout>
    )
  }

  const handleApprove = (asAssociate = false) => {
    setIsAssociateApproval(asAssociate)
    setShowApprovalModal(true)
  }

  const handleReject = () => {
    setShowRejectionModal(true)
  }

  const confirmApproval = async () => {
    setIsSubmitting(true)
    try {
      await updateApplication({
        id: Number(id),
        data: {
          approve: !isAssociateApproval,
          approve_as_associate: isAssociateApproval
        }
      }).unwrap()
      setShowApprovalModal(false)
      // Small delay to ensure cache invalidation completes
      setTimeout(() => {
        navigate('/admin/pending-applications')
      }, 100)
    } catch (error) {
      console.error('Failed to approve application:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const confirmRejection = async () => {
    if (!rejectionReason.trim()) return

    setIsSubmitting(true)
    try {
      await updateApplication({
        id: Number(id),
        data: {
          reject: true,
          reject_reason: rejectionReason
        }
      }).unwrap()
      setShowRejectionModal(false)
      navigate('/admin/pending-applications')
    } catch (error) {
      console.error('Failed to reject application:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const canApprove =
    application.status === 'pending' &&
    (!application.first_approval_by ||
      (application.first_approval_by &&
        !application.second_approval_by &&
        application.first_approval_by !== user.id))

  return (
    <AdminPageLayout>
      {/* Header */}
      <div className="mb-8">
        <BackToDashboardLink className="mb-4 flex items-center" />
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-normal text-[#3C2416]">
            {application.center_name}
          </h1>
          <CenterTypeBadge centerType={application.center_type as CenterType} />
        </div>
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span>
            {application.street_address}, {application.city},{' '}
            {application.state} {application.zip_code}
          </span>
        </div>
      </div>

      {/* Top Section - Timeline, Buttons, and Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Timeline Card */}
        <div className="bg-white rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#A6410A] mb-4">
            Application Timeline
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#3C2416]">
                  Application Submitted
                </p>
                <p className="text-sm text-gray-500">
                  {application.created_at
                    ? new Date(application.created_at).toLocaleDateString()
                    : 'Date not available'}
                </p>
              </div>
            </div>

            {application.first_approval_by && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#3C2416]">First Approval</p>
                  <p className="text-sm text-gray-500">
                    {application.first_approval_at
                      ? new Date(
                          application.first_approval_at
                        ).toLocaleDateString()
                      : 'Date not available'}
                  </p>
                </div>
              </div>
            )}

            {application.first_approval_by &&
              !application.second_approval_by && (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <ClockIcon className="w-6 h-6 text-[#AF8A48]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#3C2416]">
                      Awaiting Second Approval
                    </p>
                    <p className="text-sm text-gray-500">Pending review</p>
                  </div>
                </div>
              )}

            {application.second_approval_by && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#3C2416]">Second Approval</p>
                  <p className="text-sm text-gray-500">
                    {application.second_approval_at
                      ? new Date(
                          application.second_approval_at
                        ).toLocaleDateString()
                      : 'Date not available'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons and Patient Stats Card */}
        <div className="lg:col-span-2 bg-white rounded-lg p-6">
          {/* Action Buttons */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#3C2416] mb-4">
              Application Actions
            </h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className={`flex-1 px-6 py-3 rounded font-medium transition-colors ${
                  canApprove
                    ? 'bg-[#142841] text-white hover:bg-[#0F1E30] cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={() => handleApprove(false)}
                disabled={!canApprove}
              >
                Approve
              </button>
              <button
                className={`flex-1 px-6 py-3 rounded font-medium transition-colors ${
                  canApprove
                    ? 'bg-[#A0B2B4] text-white hover:bg-[#8B9DA0] cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={() => handleApprove(true)}
                disabled={!canApprove}
              >
                Approve as Associate
              </button>
              <button
                className={`flex-1 px-6 py-3 rounded font-medium transition-colors ${
                  application.status === 'pending'
                    ? 'bg-[#A6410A] text-white hover:bg-[#894d35] cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={handleReject}
                disabled={application.status !== 'pending'}
              >
                Reject
              </button>
            </div>
          </div>

          {/* Patient Stats and Directors Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Patient Stats */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-[#3C2416]">
                    {totalSCPatients}
                  </div>
                  <div className="text-sm text-gray-600">SC Patients</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#3C2416]">
                    {totalSCAPatients}
                  </div>
                  <div className="text-sm text-gray-600">SCA Patients</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-3xl font-bold text-[#3C2416] text-center">
                  {totalOtherPatients}
                </div>
                <div className="text-sm text-gray-600 text-center">
                  Other Patients
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-3xl font-bold text-[#3C2416] text-center">
                  {application.employees?.length || 0}
                </div>
                <div className="text-sm text-gray-600 text-center">
                  Dedicated Outpatient Staff
                </div>
              </div>
            </div>

            {/* Directors */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-[#3C2416] mb-4">
                Center Director
              </h3>
              {application.director_name ? (
                <p className="text-sm text-gray-700 mb-4">
                  {application.director_name}
                </p>
              ) : (
                <p className="text-sm text-gray-500 mb-4">Not specified</p>
              )}

              <h3 className="font-semibold text-[#3C2416] mb-2">
                Center Co-Director
              </h3>
              {application.co_director_name ? (
                <p className="text-sm text-gray-700">
                  {application.co_director_name}
                </p>
              ) : (
                <p className="text-sm text-gray-500">Not specified</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Middle Section - Clinical Space and Written Protocols */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Clinical Space */}
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#A6410A] mb-4">
            Clinic Space
          </h3>
          <div className="space-y-1">
            <StatusRow
              label="Dedicated Clinic Space?"
              value={application.dedicated_clinic_space}
            />
            <StatusRow
              label="DCS Shared with a Cancer Center?"
              value={application.dcs_shared_with_cancer_center}
            />
            <StatusRow
              label="DCS is an Independent Clinic Space?"
              value={application.indep_clinic_space}
            />
            <StatusRow
              label="DCS Shared with Something Else?"
              value={application.dcs_shared_with_other}
            />
            <StatusRow
              label="Multidisciplinary?"
              value={application.multidisciplinary}
            />
            <StatusRow
              label="Infusion Area?"
              value={application.infusion_area}
            />
            <StatusRow
              label="IA Shared With a Cancer Center?"
              value={application.inf_area_shared_with_cancer_center}
            />
            <StatusRow
              label="IA Contains a Dedicated Day Hospital?"
              value={application.inf_area_dedicated_day_hospital}
            />
            <StatusRow
              label="IA Shared with Something Else?"
              value={application.inf_area_shared_with_other}
            />
            <StatusRow
              label="Observation Unit?"
              value={application.observation_unit}
            />
          </div>
        </div>

        {/* Written Protocols */}
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#A6410A] mb-4">
            Written Protocols and Documentation
          </h3>
          <div className="space-y-1">
            <StatusRow
              label="Written Protocol for Acute and Chronic Sickle Cell Disease?"
              value={application.written_proc_for_sickle_cell_disease}
            />
            <StatusRow
              label="Emergency Department Protocols for Sickle Cell Disease?"
              value={application.emergency_dept_protocols_for_scd}
            />
            <StatusRow
              label="Protocol for Transition to Adult Care?"
              value={application.trans_to_adult_protocol}
            />
            <StatusRow
              label="Protocol for Tracking Newborn Screening Results?"
              value={application.tracking_newborn_screen_protocol}
            />
            <StatusRow
              label="Protocol for Tracking Quality Metrics?"
              value={application.tracking_quality_metrics_protocol}
            />
          </div>
        </div>
      </div>

      {/* Bottom Section - Employees and Agreements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Employees Table */}
        <EmployeesTable employees={application.employees || []} />

        {/* Agreements */}
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#A6410A] mb-4">
            Agreements
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#3C2416]">Agreed to GRNDaD</span>
              <StatusIcon value={application.agree_to_grndad} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#3C2416]">Agreed to Bylaws</span>
              <StatusIcon value={application.agree_to_bylaws} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#3C2416]">Document Signed</span>
              <StatusIcon value={application.document_signed} />
            </div>
            {application.signed_by && (
              <div className="mt-2 pt-2 border-t">
                <p className="text-sm text-gray-600">
                  Signed by: {application.signed_by}
                </p>
              </div>
            )}
            {application.application_token && (
              <div className="mt-2 pt-2 border-t">
                <p className="text-sm text-gray-600">
                  Application Token: {application.application_token}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-[#3C2416] mb-4">
              {isAssociateApproval
                ? 'Approve as Associate Center'
                : 'Approve Center Application'}
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to{' '}
              {isAssociateApproval
                ? 'approve this application as an associate center'
                : 'approve this application'}
              ?
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmApproval}
                className="flex-1 px-4 py-2 bg-[#142841] text-white rounded hover:bg-[#0F1E30] transition-colors disabled:opacity-50 cursor-pointer"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-[#3C2416] mb-4">
              Reject Application
            </h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this application:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#142841] focus:border-transparent"
              placeholder="Enter rejection reason..."
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setShowRejectionModal(false)
                  setRejectionReason('')
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmRejection}
                className="flex-1 px-4 py-2 bg-[#A6410A] text-white rounded hover:bg-[#894d35] transition-colors disabled:opacity-50"
                disabled={isSubmitting || !rejectionReason.trim()}
              >
                {isSubmitting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  )
}
